/**
 * Page-width layout for every dashboard route.
 *
 * The shell (`components/app-shell-bar.tsx`) caps its rows at
 * `--content-max` plus a `--space-5` gutter each side. A page container that
 * carries the same gutter as its own padding uses `PAGE_FRAME_MAX`; one that
 * sits inside an already-padded parent uses `PAGE_CONTENT_MAX`. Either way its
 * edges line up with the header's — and neither leaves a narrow column floating
 * in a wide screen, which is what the hardcoded narrow caps used to do.
 */
import type { CSSProperties } from 'react';

/** Content box width: for a container with no side padding of its own. */
export const PAGE_CONTENT_MAX = 'var(--content-max)';

/** Border box width: for a container padded by `--space-5` each side. */
export const PAGE_FRAME_MAX = 'calc(var(--content-max) + 2 * var(--space-5))';

/**
 * The page frame for a route whose header and body each carry their own
 * `--space-5` side padding: as wide as the shell, no side padding of its own,
 * so those children land exactly on the header's edges instead of a gutter
 * further in. Vertical rhythm matches the shared kit's `pageStyle`.
 */
export const dashboardPageStyle: CSSProperties = {
  maxWidth: PAGE_FRAME_MAX,
  margin: '0 auto',
  padding: 'var(--space-6) 0',
};

/**
 * A set of cards that fills the row: as many 22.5rem-or-wider columns as fit,
 * one column on a phone (the `min(100%, …)` keeps the narrowest phone from
 * overflowing).
 */
export const cardGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 22.5rem), 1fr))',
  gap: 'var(--space-4)',
  alignItems: 'start',
};

/**
 * Settings / form sections side by side once there is room for two 27.5rem
 * columns (a laptop-width viewport), stacked below that — so no field becomes
 * absurdly long and no half of the screen sits empty.
 */
export const sectionGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 27.5rem), 1fr))',
  gap: 'var(--space-5)',
  alignItems: 'start',
};

/** A grid child that spans every column — a master switch or save bar above a two-column form. */
export const gridFullRowStyle: CSSProperties = { gridColumn: '1 / -1' };
